import React, { useEffect } from "react";
import { useState, useContext } from "react";
import { TextField, Button, Stack, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { set, useForm } from "react-hook-form";
import ReCAPTCHA from "react-google-recaptcha";
import { UserContext } from "../utilities/UserContext";
import MLDCReactLoadingIndicator from "@metlife/mldc-react-loading-indicator";

const RegistrationForm = () => {

  const { userId } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [recpatchaError,setRecaptchaError] = useState(null);
  const [showRecaptcha, setShowRecaptcha] = useState(true);
  const [reCaptchaSiteKey, setReCaptchaSiteKey] = useState(null);
  const [pingRedirectionUrl, setPingRedirectionUrl] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      tin: "",
      zip: "",
      email: "",
    },
  });

  useEffect(() => {
    const fetchConfiguration = async () => {
      const response = await fetch("/mdpr/get-configuration", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const configValues = await response.json();
      setReCaptchaSiteKey(configValues.RECAPTCHA_SITE_KEY);
      setPingRedirectionUrl(configValues.PING_REDIRECTION_URL);
    }
    fetchConfiguration();

  }, []);

  const getApiBaseUrl = () => {
    return process.env.REACT_APP_REGISTRATION_SERVICE_URL;
  };

  const onSubmit = async (formData) => {
    setLoading(true);

    if (!recaptchaValue) {
        setRecaptchaError("Please complete the ReCAPTCHA");
        setLoading(false);
        return;
    }

    const url = `${getApiBaseUrl()}/registration`;
    formData.userId = userId;
    formData.recaptcha = recaptchaValue;

    try {

        const response = await fetch('/mdpr/v1/registration/api/v1/ping/registration', {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(formData),
        });

        let data = await response.json().catch((error) => {
            return null;
        });

        handleApiResponse(response, data, pingRedirectionUrl);
    } catch (error) {
        navigateToSupport("technical");
    } finally {
        setLoading(false);
    }
};

const handleApiResponse = (response, data, pingRedirectionUrl) => {
    if (!response.ok) {
        navigateToSupport("technical");
    } else {
        if (data?.status === "Success") {
            const guid = `&mdpr_cd=${encodeURIComponent(data.data)}`;
            window.location.replace(`${pingRedirectionUrl}${guid}`);
        } else {
            navigateToSupport("registration");
        }
    }
};

const navigateToSupport = (errorType) => {
    setShowRecaptcha(false);
    navigate("/public/registrationContactSupport", {
        state: { errorType },
    });
};

   const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
    setRecaptchaError(null);
  };

  return (
    <>
      <Box
        sx={{
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          backgroundColor: "white",
          padding: "20px",
          borderRadius: "8px",
          width: "20", // Adjust the width as needed
          marginTop: "20px",
          marginBottom: "20px",
        }}
      >
        <h1
          aria-label="MetDental Registration"
          title="MetDental Registration"
          className=""
          style={{
            textAlign: "center",
            fontSize: "22px",
            margin: "1rem",
            fontFamily: "Georgia",
          }}
        >
          MetDental Registration
        </h1>
        <p
          style={{
            width: "100%",
            paddingInline: "2rem",
            fontStyle: "italic",
            fontSize: "14px",
          }}
        >
          All fields are required unless otherwise noted.
        </p>
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          style={{
            backgroundColor: "#ffffff",
            padding: "20px",
            borderRadius: "8px",
          }}
        >
          <Stack spacing={2} width={300}>
            <TextField
              label="First Name"
              variant="outlined"
              InputProps={{
                style: { borderRadius: "0px" }, // Apply custom border radius
              }}
              {...register("firstName", { required: "First Name is required" })}
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              disabled={loading} // Disable the field when loading
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "black", // Default border color
                  },
                  "&:hover fieldset": {
                    borderColor: "black", // Border color on hover
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "black", // Border color when focused
                  },
                },
              }}
            />
            <TextField
              label="Last Name"
              variant="outlined"
              InputProps={{
                style: { borderRadius: "0px" }, // Apply custom border radius
              }}
              {...register("lastName", { required: "Last Name is required" })}
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              disabled={loading} // Disable the field when loading
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "black", // Default border color
                  },
                  "&:hover fieldset": {
                    borderColor: "black", // Border color on hover
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "black", // Border color when focused
                  },
                },
              }}
            />
            <TextField
              label="Tax ID"
              variant="outlined"
              InputProps={{
                style: { borderRadius: "0px" }, // Apply custom border radius
              }}
              {...register("tin", {
                required: "Tax ID is required",
                pattern: {
                  value: /^[0-9]+$/,
                  message: "Invalid Tax ID",
                },
              })}
              error={!!errors.tin}
              helperText={errors.tin?.message}
              disabled={loading} // Disable the field when loading
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "black", // Default border color
                  },
                  "&:hover fieldset": {
                    borderColor: "black", // Border color on hover
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "black", // Border color when focused
                  },
                },
              }}
            />
            <TextField
              label="Zip Code"
              variant="outlined"
              InputProps={{
                style: { borderRadius: "0px" }, // Apply custom border radius
              }}
              {...register("zip", {
                required: "Zip Code is required",
                pattern: {
                  value: /^[0-9]{5}$/i,
                  message: "Invalid Zip Code",
                },
              })}
              error={!!errors.zip}
              helperText={errors.zip?.message}
              disabled={loading} // Disable the field when loading
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "black", // Default border color
                  },
                  "&:hover fieldset": {
                    borderColor: "black", // Border color on hover
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "black", // Border color when focused
                  },
                },
              }}
            />
            <TextField
              label="Email ID"
              variant="outlined"
              InputProps={{
                style: { borderRadius: "0px" }, // Apply custom border radius
              }}
              {...register("email", {
                required: "Email ID is required",
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: "Invalid email address",
                },
              })}
              error={!!errors.email}
              helperText={errors.email?.message}
              disabled={loading} // Disable the field when loading
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "black", // Default border color
                  },
                  "&:hover fieldset": {
                    borderColor: "black", // Border color on hover
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "black", // Border color when focused
                  },
                },
              }}
            />
            <div className="recaptcha-container">
            {reCaptchaSiteKey && showRecaptcha && ( 
              <ReCAPTCHA sitekey={reCaptchaSiteKey}
              onChange={handleRecaptchaChange}
              data-testid="recaptcha"
            
               />
              )}
            </div>
            <div>
            {recpatchaError && <p style={{ color: 'red',fontSize: '12px',textAlign: 'left' }}>{"Enter Captcha"}</p>}
            </div>
            <Button type="submit" variant="contained" disabled={loading}>
              Next
            </Button>
          </Stack>
        </form>
        {loading && <MLDCReactLoadingIndicator />}{" "}
        {/* Show the loading indicator when loading */}
      </Box>
    </>
  );
};

export default RegistrationForm;
